import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllOpps,
  createOpp,
  updateOpp,
  deleteOpp,
  getAllLocations,
  getAllForms,
  getAllTeachers,
  getOppsByBaseID,
  getLocationsByBaseId,
  getFormsByBaseId,
  getTeachersByBaseId,
} from "../services/firebaseService";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { LocationContext } from "../LocationContext";
import { UserContext } from "../UserContext";

const AdminOpps = () => {
  const navigate = useNavigate();
  const { baseId } = useContext(LocationContext);
  const { permissions } = useContext(UserContext);
  const [opps, setOpps] = useState([]);
  const [locations, setLocations] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [oppToDelete, setOppToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [forms, setForms] = useState([]);

  useEffect(() => {
    fetchOpps();
    fetchLocations();
    fetchForms();
    fetchTeachers();
  }, [baseId, permissions]);

  const fetchOpps = async () => {
    setIsLoading(true);
    try {
      let oppsSnapshot;
      if (permissions?.ALL) {
        // console.log("Fetching all opps");
        oppsSnapshot = await getAllOpps();
      } else {
        if (baseId) {
          // console.log("Fetching opps by base ID:", baseId);
          oppsSnapshot = await getOppsByBaseID(baseId);
        } else {
          // console.log("No base ID, fetching empty array");
          oppsSnapshot = [];
        }
      }
      setOpps(oppsSnapshot);
    } catch (error) {
      setErrorMessage("Error fetching opportunities: " + error.message);
    }
    setIsLoading(false);
  };

  const fetchLocations = async () => {
    try {
      let locationsSnapshot;
      if (permissions?.ALL) {
        locationsSnapshot = await getAllLocations();
        // console.log("locationsSnapshot", locationsSnapshot);
      } else {
        if (baseId) {
          locationsSnapshot = await getLocationsByBaseId(baseId);
        } else {
          locationsSnapshot = [];
        }
      }
      setLocations(locationsSnapshot);
    } catch (error) {
      setErrorMessage("Error fetching locations: " + error.message);
    }
  };

  const fetchForms = async () => {
    try {
      let formsSnapshot;
      if (permissions?.ALL) {
        formsSnapshot = await getAllForms();
      } else {
        if (baseId) {
          formsSnapshot = await getFormsByBaseId(baseId);
        } else {
          formsSnapshot = [];
        }
      }
      setForms(formsSnapshot);
    } catch (error) {
      setErrorMessage("Error fetching forms: " + error.message);
    }
  };

  const fetchTeachers = async () => {
    try {
      let teachersSnapshot;
      if (permissions?.ALL) {
        teachersSnapshot = await getAllTeachers();
      } else {
        if (baseId) {
          teachersSnapshot = await getTeachersByBaseId(baseId);
        } else {
          teachersSnapshot = [];
        }
      }
      setTeachers(teachersSnapshot || []); // Ensure it's an array
    } catch (error) {
      setErrorMessage("Error fetching teachers: " + error.message);
    }
  };

  const handleEdit = (opp) => {
    setIsEditing(true);
    setEditingOpp({
      ...opp,
      teachers: opp.teachers || [], // Ensure teachers is initialized as array
    });
    // console.log("Editing opportunity:", editingOpp);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    try {
      await deleteOpp(id);
      fetchOpps();
    } catch (error) {
      setErrorMessage("Error deleting opportunity: " + error.message);
    }
  };

  const handleSubmit = async () => {
    try {
      // Check if teachers array is empty
      if (!editingOpp.teachers || editingOpp.teachers.length === 0) {
        setErrorMessage("Please select at least one teacher");
        return;
      }

      const oppWithBaseId = {
        ...editingOpp,
        baseId: baseId,
      };

      if (editingOpp.id) {
        // console.log("Updating opportunity:", editingOpp);
        await updateOpp(editingOpp.id, oppWithBaseId);
      } else {
        await createOpp(oppWithBaseId);
      }
      setIsEditing(false);
      setEditingOpp(null);
      fetchOpps();
    } catch (error) {
      setErrorMessage("Error saving opportunity: " + error.message);
    }
  };

  const handleAddOpp = () => {
    setIsEditing(true);
    setEditingOpp({
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      location: "",
      form: "",
      teachers: [], // Initialize as empty array
      seats: 0,
      baseId: baseId,
    });
  };

  if (isLoading) {
    return <div className="text-center">Loading...</div>;
  }

  return (
    <>
      <div className="mb-4">
        <button
          onClick={() => window.history.back()}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          <ArrowLeftIcon className="h-5 w-5 inline-block mr-2" />
          Back
        </button>
      </div>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
            Opportunities
          </h1>
          <div className="mb-4">
            <button
              onClick={handleAddOpp}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              <PlusIcon className="h-5 w-5 inline-block mr-2" />
              Add Opportunity
            </button>
          </div>
          {isEditing && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setIsSaveModalOpen(true);
              }}
              className="mb-8 bg-white dark:bg-gray-800 shadow-md rounded px-8 pt-6 pb-8 mb-4"
            >
              <div className="mb-4">
                <label
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                  htmlFor="name"
                >
                  Name
                </label>
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline dark:bg-gray-700"
                  id="name"
                  type="text"
                  value={editingOpp.name}
                  onChange={(e) =>
                    setEditingOpp({ ...editingOpp, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                  htmlFor="description"
                >
                  Description
                </label>
                <textarea
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline dark:bg-gray-700"
                  id="description"
                  value={editingOpp.description}
                  onChange={(e) =>
                    setEditingOpp({
                      ...editingOpp,
                      description: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                  htmlFor="startDate"
                >
                  Start Date
                </label>
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline dark:bg-gray-700"
                  id="startDate"
                  type="datetime-local"
                  value={editingOpp.startDate}
                  onChange={(e) =>
                    setEditingOpp({ ...editingOpp, startDate: e.target.value })
                  }
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                  htmlFor="endDate"
                >
                  End Date
                </label>
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline dark:bg-gray-700"
                  id="endDate"
                  type="datetime-local"
                  value={editingOpp.endDate}
                  onChange={(e) =>
                    setEditingOpp({ ...editingOpp, endDate: e.target.value })
                  }
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                  htmlFor="location"
                >
                  Location
                </label>
                <select
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline dark:bg-gray-700"
                  id="location"
                  value={editingOpp.location}
                  onChange={(e) =>
                    setEditingOpp({ ...editingOpp, location: e.target.value })
                  }
                  required
                >
                  <option value="">Select a location</option>
                  {locations?.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                  htmlFor="form"
                >
                  Request Form
                </label>
                <select
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline dark:bg-gray-700"
                  id="form"
                  value={editingOpp.form}
                  onChange={(e) =>
                    setEditingOpp({ ...editingOpp, form: e.target.value })
                  }
                >
                  <option value="">Select a form</option>
                  {forms?.map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                  htmlFor="teachers"
                >
                  Teachers *
                </label>
                {teachers && teachers?.length > 0 ? (
                  <div className="flex gap-2 mb-4">
                    <select
                      className="shadow appearance-none border rounded flex-1 py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline dark:bg-gray-700"
                      id="teachers"
                      required={editingOpp?.teachers?.length === 0}
                    >
                      <option value="">Select a teacher</option>
                      {teachers
                        .filter(
                          (teacher) =>
                            !editingOpp?.teachers.includes(teacher.id)
                        )
                        ?.map((teacher) => (
                          <option key={teacher.id} value={teacher.id}>
                            <div className="flex items-center">
                              <img
                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/Default_pfp.svg/2048px-Default_pfp.svg.png"
                                alt={teacher.name}
                                className="w-6 h-6 rounded-full mr-2"
                              />
                              {teacher.name}
                            </div>
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      onClick={() => {
                        const select = document.getElementById("teachers");
                        const selectedId = select.value;
                        if (
                          selectedId &&
                          !editingOpp?.teachers.includes(selectedId)
                        ) {
                          setEditingOpp({
                            ...editingOpp,
                            teachers: [...editingOpp.teachers, selectedId],
                          });
                          select.value = ""; // Reset selection
                        }
                      }}
                    >
                      Add
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-500 dark:text-gray-400">
                    No trainers available
                  </div>
                )}

                {/* Add validation message */}
                {editingOpp?.teachers?.length === 0 && (
                  <p className="text-red-500 text-xs italic">
                    At least one teacher is required
                  </p>
                )}

                {editingOpp?.teachers?.length > 0 && (
                  <div className="shadow border rounded w-full p-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700">
                    {/* {// console.log("editingOpp:", editingOpp)} */}
                    {teachers
                      .filter((teacher) =>
                        editingOpp?.teachers?.includes(teacher.id)
                      )
                      ?.map((teacher, index, array) => (
                        <div
                          key={teacher.id}
                          className={`flex items-center justify-between p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
                            index !== array.length - 1
                              ? "border-b border-white dark:border-gray-600"
                              : ""
                          }`}
                        >
                          <div className="flex items-center">
                            <img
                              src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/Default_pfp.svg/2048px-Default_pfp.svg.png"
                              alt={teacher.name}
                              className="w-10 h-10 rounded-full mr-3"
                            />
                            <span>{teacher.name}</span>
                          </div>
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              setEditingOpp({
                                ...editingOpp,
                                teachers: editingOpp.teachers.filter(
                                  (id) => id !== teacher.id
                                ),
                              });
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                  htmlFor="seats"
                >
                  Number of Seats
                </label>
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline dark:bg-gray-700"
                  id="seats"
                  type="number"
                  min="0"
                  max="50"
                  value={editingOpp.seats}
                  onChange={(e) =>
                    setEditingOpp({
                      ...editingOpp,
                      seats: Math.min(50, Math.max(0, e.target.value)),
                    })
                  }
                  required
                />
              </div>
              <div className="flex items-center justify-between">
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  type="submit"
                >
                  Save
                </button>
                <button
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  onClick={() => {
                    setIsEditing(false);
                    setEditingOpp(null);
                  }}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {opps?.map((opp) => (
              <div
                key={opp.id}
                className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
              >
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    {opp.name}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    {opp.description}
                  </p>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    Start: {new Date(opp.startDate).toLocaleString()}
                  </p>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    End: {new Date(opp.endDate).toLocaleString()}
                  </p>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    Location:{" "}
                    {locations.find((loc) => loc.id === opp.location)?.name}
                  </p>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    Form:{" "}
                    {forms.find((f) => f.id === opp.form)?.name ||
                      "No form selected"}
                  </p>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    Teachers:{" "}
                    {opp.teachers && opp.teachers.length > 0
                      ? opp.teachers
                          ?.map(
                            (teacherId) =>
                              teachers.find((t) => t.id === teacherId)?.name
                          )
                          .join(", ")
                      : "No teachers assigned"}
                  </p>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    Seats: {opp.seats}
                  </p>
                  <div className="mt-4 flex space-x-3">
                    <button
                      onClick={() => handleEdit(opp)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setIsDeleteModalOpen(true);
                        setOppToDelete(opp.id);
                      }}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Save Confirmation Modal */}
      {isSaveModalOpen && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckIcon
                      className="h-6 w-6 text-green-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3
                      className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                      id="modal-title"
                    >
                      Save Changes
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to save these changes?
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    handleSubmit();
                    setIsSaveModalOpen(false);
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsSaveModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationCircleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3
                      className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                      id="modal-title"
                    >
                      Delete Opportunity
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this opportunity? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    handleDelete(oppToDelete);
                    setIsDeleteModalOpen(false);
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Error Message Modal */}
      {errorMessage && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationCircleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3
                      className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                      id="modal-title"
                    >
                      Error
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {errorMessage}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setErrorMessage("")}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminOpps;
